import { AuxMaskOptionsInterface } from '@blk/aladdin-react-components';
import { ColumnDefV2Format, CurrencyAutoSettings } from '@blk/aw-bff-typescript-models';
import { roundFloatingPointError } from '@blk/aw-utils-core';
import Decimal from 'decimal.js';
import { dequal } from 'dequal';
import { isNil, isNumber, startCase, toLower, toUpper, upperFirst } from 'lodash';
import memoizeOne from 'memoize-one';
import moment from 'moment/min/moment-with-locales';
import numeral from 'numeral';
import { normalizeMomentLocale } from './DateUtils';
import { getFxConversionRate } from './FxUtils';
import { i18nInstance } from './I18nUtils';
import { search } from './JmespathUtils';
import { multiCurrencyCache } from './MultiCurrencyCache';

export type DecimalSeparatorFormat = 'COMMA' | 'PERIOD';

interface ShorthandDefinition {
  character: string;
  multiplier: number;
}

// Supported currency, with their respective symbols
export const SupportedCurrencyMap = {
  EUR: '€',
  USD: '$',
  MXN: '$',
  GBP: '£',
  JPY: '¥',
  CHF: 'CHF',
  DKK: 'DKK',
  HKD: '$',
  AUD: '$',
  ZAR: 'ZAR',
  SGD: 'S$',
  THB: '฿',
  CAD: '$',
  NZD: '$',
  BRL: 'R$',
  AED: 'د.إ',
  ARS: '$',
  CZK: 'Kč',
  HUF: 'Ft',
  ISK: 'ISK',
  NOK: 'NOK',
  PLN: 'zł',
  RUB: '₽',
  SEK: 'SEK',
  TRY: '₺',
  TWD: '$',
  INR: '₹',
  RSD: 'RSD',
} as const;

/**
 * Since options is an object, we want to do a deep equal comparision to see if we can return the memoized formatting
 * object. This does not seriously impact performance as the options object is quite lightweight.
 */
const getNumberFormatInstance = memoizeOne(
  (language: string, options: Intl.NumberFormatOptions): Intl.NumberFormat => new Intl.NumberFormat(language, options),
  dequal
);

const toLocaleStringWithI18next = (numericValue: number, localeStringOptions: Intl.NumberFormatOptions) =>
  getNumberFormatInstance(i18nInstance.language, localeStringOptions).format(numericValue);

export const numeralFormat = (
  value: number,
  maximumFractionDigits: number,
  minimumFractionDigits?: number,
  includeSeparators = true
): string => {
  const localeStringOptions: Intl.NumberFormatOptions = {
    minimumFractionDigits: minimumFractionDigits === undefined ? maximumFractionDigits : minimumFractionDigits,
    maximumFractionDigits,
    useGrouping: includeSeparators,
  };
  return toLocaleStringWithI18next(sanitizeNumber(value, maximumFractionDigits), localeStringOptions);
};

const currencyFormat = (value: number, currency: string, includeSymbol = false): string => {
  if (!currency) {
    throw Error('currency is not defined for currency formatter');
  }
  const localeStringOptions: Intl.NumberFormatOptions = {
    style: 'currency',
    currency,
    currencyDisplay: includeSymbol ? 'symbol' : 'code',
    useGrouping: true,
  };
  const formatter = new Intl.NumberFormat(i18nInstance.language, localeStringOptions);
  // Use formatToParts to extract the fraction part
  const parts = formatter.formatToParts(value);
  const fractionPart = parts.find((part) => part.type === 'fraction');

  const localeCurrencyString: string = toLocaleStringWithI18next(
    sanitizeNumber(value, fractionPart?.value.length),
    localeStringOptions
  );
  if (!includeSymbol) {
    // strip ISO code from currency string, i.e. USD 1,000 -> 1,000
    return localeCurrencyString.replace(/[a-z]{3}/i, '').trim();
  }
  return localeCurrencyString;
};

/* This refers to the currencyAuto config under utilities. The config will be used to perform global level formatting of currency with currencyAutoFormatter*/
const currencyAutoSettings: CurrencyAutoSettings = { withoutSymbol: false };

export const getCurrencyAutoSettings = () => currencyAutoSettings;

const currencyWholeNumberFormat = (value: number, currency: string): string => {
  if (!currency) {
    throw Error('currency is not defined for currency formatter');
  }
  const localeStringOptions: Intl.NumberFormatOptions = {
    style: 'currency',
    currency,
    currencyDisplay: 'symbol',
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
    useGrouping: true,
  };
  return toLocaleStringWithI18next(sanitizeNumber(value), localeStringOptions);
};

const MIDNIGHT_UTC_REGEX = /^\d{4}-\d{2}-\d{2}T00:00:00(\.0+)?Z$/;

const dateFormat = (rawValue: number | string, format: string = 'lll', language?: string): string => {
  const value = typeof rawValue === 'string' && /^\d+$/.test(rawValue) ? Number(rawValue) : rawValue;
  const m = moment(typeof value === 'string' && MIDNIGHT_UTC_REGEX.test(value) ? value.substring(0, 10) : value);
  return language ? m.locale(normalizeMomentLocale(language)).format(format) : m.format(format);
};

/**
 * AWM-17007 -- Some client team folks are calling some columns 'numeric' even though the data is a number as a string
 * and would like an empty string to evaluate the same as nil/NaN. We have said this is not preferred, but that said,
 * this change is very minor
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- existing any
export const isNumberInvalid = (value: number) => isNil(value) || isNaN(value) || (value as any) === '';

const numericShorthandFormatter: ValueFormatter = (value: number): string =>
  isNumberInvalid(value)
    ? '-'
    : convertNumberToShorthand(value, { minimumFractionDigits: 0, maximumFractionDigits: 1 });
const currencyFormatter: CurrencyFormatter =
  (currency: string) =>
  (value: number): string =>
    isNumberInvalid(value) ? '-' : currencyFormat(value, currency, false);
const currencyWithSymbolFormatter: CurrencyFormatter =
  (currency: string) =>
  (value: number): string =>
    isNumberInvalid(value) ? '-' : currencyFormat(value, currency, true);
const currencyWithSymbolPostfixFormatter: CurrencyFormatter =
  (currency: string) =>
  (value: number): string =>
    isNumberInvalid(value) ? '-' : `${currencyFormat(value, currency, true)} ${currency}`;
const currencyAutoFormatter: CurrencyFormatter =
  (currency: string) =>
  (value: number): string =>
    isNumberInvalid(value) ? '-' : currencyFormat(value, currency, !getCurrencyAutoSettings()?.withoutSymbol);
const currencyShorthandWithSymbolFormatter: CurrencyFormatter =
  (currency: string) =>
  (value: number): string => {
    const symbol = getSupportedCurrencySymbol(currency);
    return isNumberInvalid(value)
      ? '-'
      : `${symbol}${convertNumberToShorthand(value, { minimumFractionDigits: 0, maximumFractionDigits: 1 })}`;
  };
const currencyWholeNumberWithSymbolFormatter: CurrencyFormatter =
  (currency: string) =>
  (value: number): string =>
    isNumberInvalid(value) ? '-' : currencyWholeNumberFormat(value, currency);

const expressionBasedCurrencyWithSymbolFormatter: ExpressionBasedCurrencyFormatter =
  (currency: string, currencyExpression?: string, searchObject?: object) =>
  (value: number): string => {
    let resolvedCurrency = currency;

    if (currencyExpression && searchObject) {
      try {
        const result = search(searchObject, currencyExpression);
        if (typeof result === 'string') {
          resolvedCurrency = result;
        }
      } catch (e) {
        console.error(`Error evaluating currencyExpression: ${currencyExpression}`, e);
      }
    }

    return currencyWithSymbolFormatter(resolvedCurrency)(value);
  };

export const createMultiCurrencyFormatter =
  (baseFormatter: CurrencyFormatter): CurrencyFormatter =>
  (currency: string) =>
  (value: number): string => {
    const { targetCurrency, fxRates } = multiCurrencyCache.getMultiCurrencyConfig();

    if (targetCurrency && fxRates) {
      try {
        if (isNumberInvalid(value)) {
          return '-';
        }
        const conversionRate = getFxConversionRate(currency, targetCurrency, fxRates);
        const convertedValue = value * conversionRate;
        return baseFormatter(targetCurrency)(convertedValue);
      } catch (e) {
        console.warn(
          `Unable to convert from ${currency} to ${targetCurrency}, falling back to base currency formatting.`,
          e
        );
      }
    }

    return baseFormatter(currency)(value);
  };

const stringUppercaseFormatter: StringFormatter =
  (stringValue: string) =>
  (value: number): string =>
    isNumberInvalid(value) ? '-' : toUpper(stringValue);

const stringLowercaseFormatter: StringFormatter =
  (stringValue: string) =>
  (value: number): string =>
    isNumberInvalid(value) ? '-' : toLower(stringValue);

const stringStartCaseFormatter: StringFormatter =
  (stringValue: string) =>
  (value: number): string =>
    isNumberInvalid(value) ? '-' : startCase(stringValue);

const stringUpperFirstFormatter: StringFormatter =
  (stringValue: string) =>
  (value: number): string =>
    isNumberInvalid(value) ? '-' : upperFirst(stringValue);

export const valueFormatters: ValueFormatters = {
  numericWholeNumber: (value: number): string => (isNumberInvalid(value) ? '-' : numeralFormat(value, 0)),
  numeric: (value: number): string => (isNumberInvalid(value) ? '-' : numeralFormat(value, 2)),
  numericInvalidZero: (value: number): string =>
    isNumberInvalid(value) || value === 0 ? '-' : numeralFormat(value, 2),
  numericShorthand: numericShorthandFormatter,
  numericShorthandAnalytics: (value: number): string =>
    isNumberInvalid(value)
      ? '-'
      : convertNumberToShorthand(value, { minimumFractionDigits: 0, maximumFractionDigits: 1 }, false),
  numericFourDecimals: (value: number): string => (isNumberInvalid(value) ? '-' : numeralFormat(value, 4, 4)),
  numericTwoDecimals: (value: number): string => (isNumberInvalid(value) ? '-' : numeralFormat(value, 2, 2)),
  currency: currencyFormatter,
  currencyWithSymbol: currencyWithSymbolFormatter,
  currencyWithSymbolPostfix: currencyWithSymbolPostfixFormatter,
  currencyAuto: currencyAutoFormatter,
  currencyShorthandWithSymbol: currencyShorthandWithSymbolFormatter,
  // FIXME: Analyse whether we need to support mccy for this, only use in 1 place currently
  signedCurrency:
    (currency: string) =>
    (value: number): string =>
      value > 0
        ? `+ ${valueFormatters.currencyWholeNumberWithSymbol(currency)(value)}`
        : `- ${valueFormatters.currencyWholeNumberWithSymbol(currency)(Math.abs(value))}`,
  currencyWholeNumberWithSymbol: currencyWholeNumberWithSymbolFormatter,
  multiCurrency: createMultiCurrencyFormatter(currencyFormatter),
  multiCurrencyWithSymbol: createMultiCurrencyFormatter(currencyWithSymbolFormatter),
  multiCurrencyWithSymbolPostfix: createMultiCurrencyFormatter(currencyWithSymbolPostfixFormatter),
  multiCurrencyAuto: createMultiCurrencyFormatter(currencyAutoFormatter),
  multiCurrencyShorthand: createMultiCurrencyFormatter((_currency: string) => numericShorthandFormatter),
  multiCurrencyShorthandWithSymbol: createMultiCurrencyFormatter(currencyShorthandWithSymbolFormatter),
  multiCurrencyWholeNumberWithSymbol: createMultiCurrencyFormatter(currencyWholeNumberWithSymbolFormatter),
  // The currency will be dynamically resolved via JMESPath expression from the provided searchObject
  expressionBasedCurrencyWithSymbol: expressionBasedCurrencyWithSymbolFormatter,
  percentageWithoutSymbol: (value: number): string => (isNumberInvalid(value) ? '-' : numeralFormat(value, 2)),
  percentage: (value: number): string =>
    isNumberInvalid(value) ? '-' : `${valueFormatters.percentageWithoutSymbol(value)}%`,
  decimalAsPercentage: (value: number): string => (isNumberInvalid(value) ? '-' : numeralFormat(value * 100, 2) + '%'),
  decimalAsPercentageWithoutSymbol: (value: number): string =>
    isNumberInvalid(value) ? '-' : numeralFormat(value * 100, 2),
  oneDecimalAsPercentage: (value: number): string =>
    isNumberInvalid(value) ? '-' : numeralFormat(value * 100, 1) + '%',
  oneDecimalAsPercentageWithoutSymbol: (value: number): string =>
    isNumberInvalid(value) ? '-' : numeralFormat(value * 100, 1),
  fourDecimalsAsPercentage: (value: number): string =>
    isNumberInvalid(value) ? '-' : numeralFormat(value * 100, 4) + '%',
  fourDecimalsAsPercentageWithoutSymbol: (value: number): string =>
    isNumberInvalid(value) ? '-' : numeralFormat(value * 100, 4),
  zeroDecimalsAsPercentage: (value: number): string =>
    isNumberInvalid(value) ? '-' : numeralFormat(value * 100, 0) + '%',
  zeroDecimalsAsPercentageWithoutSymbol: (value: number): string =>
    isNumberInvalid(value) ? '-' : numeralFormat(value * 100, 0),
  defaultFormatter: (value: number | string): string => (isNil(value) ? '-' : value.toString()),
  datetime: (value: number | string, language?: string): string => dateFormat(value, 'lll', language),
  time: (value: number | string, language?: string): string => dateFormat(value, 'LT', language),
  date: (value: number | string, language?: string): string => dateFormat(value, 'll', language),
  yearMonthDay: (value: number | string): string => dateFormat(value, 'YYYY-MM-DD'),
  monthDayYear: (value: number | string): string => dateFormat(value, 'MM/DD/YY'),
  stringUppercase: stringUppercaseFormatter,
  stringLowercase: stringLowercaseFormatter,
  stringStartCase: stringStartCaseFormatter,
  stringUpperFirst: stringUpperFirstFormatter,
};

export type ColumnDefValueFormatter = {
  [p in ColumnDefV2Format]: p extends
    | 'CURRENCY'
    | 'CURRENCY_WITH_SYMBOL'
    | 'CURRENCY_WITH_SYMBOL_POSTFIX'
    | 'CURRENCY_AUTO'
    | 'CURRENCY_SHORTHAND_WITH_SYMBOL'
    | 'CURRENCY_WHOLE_NUMBER_WITH_SYMBOL'
    | 'MULTI_CURRENCY'
    | 'MULTI_CURRENCY_WITH_SYMBOL'
    | 'MULTI_CURRENCY_WITH_SYMBOL_POSTFIX'
    | 'MULTI_CURRENCY_AUTO'
    | 'MULTI_CURRENCY_SHORTHAND'
    | 'MULTI_CURRENCY_SHORTHAND_WITH_SYMBOL'
    | 'MULTI_CURRENCY_WHOLE_NUMBER_WITH_SYMBOL'
    | 'EXPRESSION_BASED_CURRENCY_WITH_SYMBOL'
    ? CurrencyFormatter
    : p extends 'STRING_UPPERCASE' | 'STRING_LOWERCASE' | 'STRING_START_CASE' | 'STRING_UPPER_FIRST'
      ? StringFormatter
      : ValueFormatter;
};

export const columnDefValueFormatters: ColumnDefValueFormatter = {
  NUMERIC_WHOLE_NUMBER: valueFormatters.numericWholeNumber,
  NUMERIC: valueFormatters.numeric,
  NUMERIC_INVALID_ZERO: valueFormatters.numericInvalidZero,
  NUMERIC_SHORTHAND: valueFormatters.numericShorthand,
  NUMERIC_FOUR_DECIMALS: valueFormatters.numericFourDecimals,
  NUMERIC_TWO_DECIMALS: valueFormatters.numericTwoDecimals,
  CURRENCY: valueFormatters.currency,
  CURRENCY_WITH_SYMBOL: valueFormatters.currencyWithSymbol,
  CURRENCY_WITH_SYMBOL_POSTFIX: valueFormatters.currencyWithSymbolPostfix,
  CURRENCY_AUTO: valueFormatters.currencyAuto,
  CURRENCY_WHOLE_NUMBER_WITH_SYMBOL: valueFormatters.currencyWholeNumberWithSymbol,
  CURRENCY_SHORTHAND_WITH_SYMBOL: valueFormatters.currencyShorthandWithSymbol,
  EXPRESSION_BASED_CURRENCY_WITH_SYMBOL: valueFormatters.expressionBasedCurrencyWithSymbol,
  MULTI_CURRENCY: valueFormatters.multiCurrency,
  MULTI_CURRENCY_WITH_SYMBOL: valueFormatters.multiCurrencyWithSymbol,
  MULTI_CURRENCY_WITH_SYMBOL_POSTFIX: valueFormatters.multiCurrencyWithSymbolPostfix,
  MULTI_CURRENCY_AUTO: valueFormatters.multiCurrencyAuto,
  MULTI_CURRENCY_SHORTHAND: valueFormatters.multiCurrencyShorthand,
  MULTI_CURRENCY_SHORTHAND_WITH_SYMBOL: valueFormatters.multiCurrencyShorthandWithSymbol,
  MULTI_CURRENCY_WHOLE_NUMBER_WITH_SYMBOL: valueFormatters.multiCurrencyWholeNumberWithSymbol,
  PERCENTAGE: valueFormatters.percentage,
  PERCENTAGE_WITHOUT_SYMBOL: valueFormatters.percentageWithoutSymbol,
  DECIMAL_AS_PERCENTAGE: valueFormatters.decimalAsPercentage,
  DECIMAL_AS_PERCENTAGE_WITHOUT_SYMBOL: valueFormatters.decimalAsPercentageWithoutSymbol,
  ONE_DECIMAL_AS_PERCENTAGE: valueFormatters.oneDecimalAsPercentage,
  ONE_DECIMAL_AS_PERCENTAGE_WITHOUT_SYMBOL: valueFormatters.oneDecimalAsPercentageWithoutSymbol,
  FOUR_DECIMALS_AS_PERCENTAGE: valueFormatters.fourDecimalsAsPercentage,
  FOUR_DECIMALS_AS_PERCENTAGE_WITHOUT_SYMBOL: valueFormatters.fourDecimalsAsPercentageWithoutSymbol,
  ZERO_DECIMALS_AS_PERCENTAGE: valueFormatters.zeroDecimalsAsPercentage,
  ZERO_DECIMALS_AS_PERCENTAGE_WITHOUT_SYMBOL: valueFormatters.zeroDecimalsAsPercentageWithoutSymbol,
  DATETIME_AS_ISO_STRING: valueFormatters.datetime,
  DATE_AS_ISO_STRING: valueFormatters.date,
  TIME_AS_ISO_STRING: valueFormatters.time,
  YEAR_MONTH_DAY: valueFormatters.yearMonthDay,
  MONTH_DAY_YEAR: valueFormatters.monthDayYear,
  STRING_UPPERCASE: valueFormatters.stringUppercase,
  STRING_LOWERCASE: valueFormatters.stringLowercase,
  STRING_START_CASE: valueFormatters.stringStartCase,
  STRING_UPPER_FIRST: valueFormatters.stringUpperFirst,
  INTRADAY: valueFormatters.defaultFormatter,
};

export const setCurrencyAutoSettings = (settings: CurrencyAutoSettings) => {
  Object.assign(currencyAutoSettings, settings);
};

export function isSupportedCurrency(currencyStr: string): currencyStr is keyof typeof SupportedCurrencyMap {
  return currencyStr in SupportedCurrencyMap;
}

export function getSupportedCurrencySymbol(currencyStr: string): string {
  if (isSupportedCurrency(currencyStr)) {
    return SupportedCurrencyMap[currencyStr];
  }
  return currencyStr;
}

export function getSupportedCurrencySymbolWithDefault(
  currencyStr: string | undefined,
  defaultSymbol: (typeof SupportedCurrencyMap)[keyof typeof SupportedCurrencyMap]
): (typeof SupportedCurrencyMap)[keyof typeof SupportedCurrencyMap] {
  if (currencyStr && isSupportedCurrency(currencyStr)) {
    return SupportedCurrencyMap[currencyStr];
  }
  return defaultSymbol;
}

function getShorthandDefinitions(): ShorthandDefinition[] {
  return [
    {
      character: i18nInstance.t('c_quadrillionShorthand') ?? '',
      multiplier: 1e15,
    },
    {
      character: i18nInstance.t('c_trillionShorthand') ?? '',
      multiplier: 1e12,
    },
    {
      character: i18nInstance.t('c_billionShorthand') ?? '',
      multiplier: 1e9,
    },
    {
      character: i18nInstance.t('c_millionShorthand') ?? '',
      multiplier: 1e6,
    },
    {
      character: i18nInstance.t('c_thousandShorthand') ?? '',
      multiplier: 1e3,
    },
  ];
}

export const getNumberSeparators = () => {
  // for some locale such as "es-MX", the minimum group digits is 2, e.g. number 1000 will be "1000", number 10000 will be "10,000".
  const localeString = numeralFormat(10000.1, 1, 1, true);
  return {
    thousandsSeparator: localeString.charAt(2),
    decimalSeparator: localeString.charAt(6),
  };
};

export const hasLeadingCurrencySymbol = (value: string): boolean => {
  const currencyList = Object.values(SupportedCurrencyMap) as string[];
  if (isNaN(parseInt(value.charAt(0))) && currencyList.includes(value.charAt(0))) {
    return true;
  }
  return currencyList.includes(value.substring(0, 3));
};
export const stringLooksLikeLocalizedNumber = (unknownString: string): boolean => {
  const { decimalSeparator, thousandsSeparator } = getNumberSeparators();
  const numberRegex = new RegExp(
    `^[ (+-]?(\\d+[${thousandsSeparator}])*(\\d+)([${decimalSeparator}e][-\\d ]?[\\d]*)?%?[ )]*$`
  );
  const hasLeadingSymbol = hasLeadingCurrencySymbol(unknownString);
  return !!unknownString.match(numberRegex) || hasLeadingSymbol;
};

function getShorthandMultiplier(value: string): number {
  const lastCharacterUppercased = value.slice(value.length - 1, value.length).toUpperCase();
  const shorthandDefinition = getShorthandDefinitions().find(
    (def) => def.character.toUpperCase() === lastCharacterUppercased
  );
  return shorthandDefinition ? shorthandDefinition.multiplier : 1;
}

interface MaskOptionsForCurrencyInputMaskType {
  forcePositiveValue?: boolean;
}
export const getMaskOptionsForCurrencyInputMask = (
  options: MaskOptionsForCurrencyInputMaskType = {}
): AuxMaskOptionsInterface => {
  const { thousandsSeparator, decimalSeparator } = getNumberSeparators();
  return {
    mask: Number,
    thousandsSeparator,
    scale: 2,
    padFractionalZeros: false,
    normalizeZeros: false,
    radix: decimalSeparator,
    mapToRadix: [decimalSeparator],
    min: !options.forcePositiveValue ? Number.MIN_SAFE_INTEGER : 0,
    max: Number.MAX_SAFE_INTEGER,
  };
};

const stripNonNumerics = /[^0-9-.e]/g;

export const parseLocaleStringToNumber = (
  numString: string | number,
  decimalSeparatorFormat?: DecimalSeparatorFormat
): number => {
  if (typeof numString === 'number') {
    return numString;
  }
  const { decimalSeparator, thousandsSeparator } = decimalSeparatorFormat
    ? {
        decimalSeparator: decimalSeparatorFormat === 'COMMA' ? ',' : '.',
        thousandsSeparator: decimalSeparatorFormat === 'COMMA' ? '.' : ',',
      }
    : getNumberSeparators();

  // Strip away currency symbol
  const hasLeadingSymbol = hasLeadingCurrencySymbol(numString);
  if (hasLeadingSymbol) {
    numString = numString.substring(1).toString();
  }
  // Strip away thousands separators
  const thousandsGreedy = new RegExp(`[${thousandsSeparator}]`, 'g');
  let stringWithStandardSeparators = numString.replace(thousandsGreedy, '');

  // Convert non-period decimal separator to a period
  if (decimalSeparator !== '.') {
    const decimalSeparatorMatches = stringWithStandardSeparators.match(new RegExp(`[${decimalSeparator}]`, 'g')) || [];
    const decimalSeparatorCount = decimalSeparatorMatches.length;

    if (decimalSeparatorCount > 1) {
      // Multiple occurrences means it's being used as thousands separator - strip all
      stringWithStandardSeparators = stringWithStandardSeparators.replace(new RegExp(`[${decimalSeparator}]`, 'g'), '');
    } else if (decimalSeparatorCount === 1) {
      // Single occurrence - check if it's being used as thousands separator
      const thousandsPattern = new RegExp(`[${decimalSeparator}]\\d{3}(?:[${decimalSeparator}]\\d{3})*$`);
      if (thousandsPattern.test(stringWithStandardSeparators)) {
        // It's being used as thousands separator - strip it
        stringWithStandardSeparators = stringWithStandardSeparators.replace(decimalSeparator, '');
      } else {
        stringWithStandardSeparators = stringWithStandardSeparators.replace(decimalSeparator, '.');
      }
    }
  }

  // Handle shorthand notation e.g. 10M for 10,000,000
  const multiplier = getShorthandMultiplier(stringWithStandardSeparators);
  let cleanString = stringWithStandardSeparators.replace(stripNonNumerics, '');
  let numValue = parseFloat(cleanString);
  // In accounting format () is negative
  if (/^\(.+\)$/.exec(numString)) {
    numValue = numValue * -1;
  }
  return numValue * multiplier;
};

export function convertNumberToShorthand(
  value: number,
  localeOptions: Intl.NumberFormatOptions = {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  },
  shouldFormatSmallNumbers: boolean = true // for numbers < 1000, there will not be any number formatting applied
): string {
  const absValue = Math.abs(value);
  if (absValue === Infinity || isNaN(absValue)) {
    return '-';
  }
  const shorthandDefinition = getShorthandDefinitions().find((def) => absValue >= def.multiplier);
  if (shorthandDefinition) {
    return `${toLocaleStringWithI18next(value / shorthandDefinition.multiplier, localeOptions)}${
      shorthandDefinition.character
    }`;
  } else if (!shouldFormatSmallNumbers) {
    return value?.toString() ?? '-';
  } else if (absValue > 0) {
    return toLocaleStringWithI18next(value, localeOptions);
  }
  return '0';
}

export function getAbsoluteDiffFormatter(
  currVal: number,
  prevVal: number,
  options?: { prefix?: string; suffix?: string }
): {
  sign: string;
  value: string;
  prefix: string;
  suffix: string;
} {
  const { prefix, suffix } = {
    prefix: '',
    suffix: '',
    ...options,
  };
  const rawVal = roundFloatingPointError(currVal - prevVal);
  const sign = rawVal < 0 ? '-' : '+';
  const value = convertNumberToShorthand(Math.abs(rawVal));
  return { sign, value, prefix, suffix };
}

export function getPercentageDiffFormatter(
  currVal: number,
  prevVal: number,
  noFixDecimal?: boolean
): {
  sign: string;
  value: string;
  symbol: string;
} {
  const rawVal = prevVal ? roundFloatingPointError(((currVal - prevVal) / prevVal) * 100) : NaN;
  const sign = rawVal < 0 ? '-' : '+';
  // set min decimal places to zero if noFixDecimal is true, otherwise fix to two decimal places by default
  const value = numeralFormat(Math.abs(rawVal), 2, noFixDecimal ? 0 : undefined);
  return { sign, value, symbol: '%' };
}

export type ValueFormatterName =
  | 'numericWholeNumber'
  | 'numeric'
  | 'numericInvalidZero'
  | 'numericShorthand'
  | 'numericShorthandAnalytics'
  | 'numericFourDecimals'
  | 'numericTwoDecimals'
  | 'currency'
  | 'currencyWithSymbol'
  | 'currencyWithSymbolPostfix'
  | 'currencyShorthandWithSymbol'
  | 'currencyWholeNumberWithSymbol'
  | 'multiCurrency'
  | 'multiCurrencyWithSymbol'
  | 'multiCurrencyWithSymbolPostfix'
  | 'multiCurrencyAuto'
  | 'multiCurrencyShorthand'
  | 'multiCurrencyShorthandWithSymbol'
  | 'multiCurrencyWholeNumberWithSymbol'
  | 'expressionBasedCurrencyWithSymbol'
  | 'percentage'
  | 'percentageWithoutSymbol'
  | 'decimalAsPercentage'
  | 'decimalAsPercentageWithoutSymbol'
  | 'oneDecimalAsPercentage'
  | 'oneDecimalAsPercentageWithoutSymbol'
  | 'defaultFormatter'
  | 'datetime'
  | 'time'
  | 'date';

export type ValueFormatter = (value: number) => string;
export type FlexibleValueFormatter = (value: number | string, language?: string) => string;
export type CurrencyFormatter = (currency: string) => ValueFormatter;
export type StringFormatter = (value: string) => ValueFormatter;

export type ExpressionBasedCurrencyFormatter = (
  currency: string,
  currencyExpression?: string,
  searchObject?: object
) => ValueFormatter;
export type ValueFormatters = {
  numericWholeNumber: ValueFormatter;
  numeric: ValueFormatter;
  numericInvalidZero: ValueFormatter;
  numericShorthand: ValueFormatter;
  numericShorthandAnalytics: ValueFormatter;
  numericFourDecimals: ValueFormatter;
  numericTwoDecimals: ValueFormatter;
  currency: CurrencyFormatter;
  currencyWithSymbol: CurrencyFormatter;
  currencyWithSymbolPostfix: CurrencyFormatter;
  currencyAuto: CurrencyFormatter;
  currencyShorthandWithSymbol: CurrencyFormatter;
  currencyWholeNumberWithSymbol: CurrencyFormatter;
  expressionBasedCurrencyWithSymbol: ExpressionBasedCurrencyFormatter;
  multiCurrency: CurrencyFormatter;
  multiCurrencyWithSymbol: CurrencyFormatter;
  multiCurrencyWithSymbolPostfix: CurrencyFormatter;
  multiCurrencyAuto: CurrencyFormatter;
  multiCurrencyShorthand: CurrencyFormatter;
  multiCurrencyShorthandWithSymbol: CurrencyFormatter;
  multiCurrencyWholeNumberWithSymbol: CurrencyFormatter;
  percentageWithoutSymbol: ValueFormatter;
  percentage: ValueFormatter;
  decimalAsPercentage: ValueFormatter;
  decimalAsPercentageWithoutSymbol: ValueFormatter;
  oneDecimalAsPercentage: ValueFormatter;
  oneDecimalAsPercentageWithoutSymbol: ValueFormatter;
  fourDecimalsAsPercentage: ValueFormatter;
  fourDecimalsAsPercentageWithoutSymbol: ValueFormatter;
  zeroDecimalsAsPercentage: ValueFormatter;
  zeroDecimalsAsPercentageWithoutSymbol: ValueFormatter;
  datetime: FlexibleValueFormatter;
  date: FlexibleValueFormatter;
  time: FlexibleValueFormatter;
  yearMonthDay: FlexibleValueFormatter;
  monthDayYear: FlexibleValueFormatter;
  defaultFormatter: FlexibleValueFormatter;
  signedCurrency: CurrencyFormatter;
  stringUppercase: StringFormatter;
  stringLowercase: StringFormatter;
  stringStartCase: StringFormatter;
  stringUpperFirst: StringFormatter;
};

export const isValueFormatterName = (s: string): s is ValueFormatterName => valueFormatters.hasOwnProperty(s);

export interface SelectFormatterArgs {
  currency?: string;
  format?: ColumnDefV2Format;
}

const currencyFormatters: (ValueFormatter | CurrencyFormatter)[] = [
  valueFormatters.currency,
  valueFormatters.currencyWithSymbol,
  valueFormatters.currencyWithSymbolPostfix,
  valueFormatters.currencyAuto,
  valueFormatters.currencyShorthandWithSymbol,
  valueFormatters.currencyWholeNumberWithSymbol,
  valueFormatters.multiCurrency,
  valueFormatters.multiCurrencyWithSymbol,
  valueFormatters.multiCurrencyWithSymbolPostfix,
  valueFormatters.multiCurrencyAuto,
  valueFormatters.multiCurrencyShorthand,
  valueFormatters.multiCurrencyShorthandWithSymbol,
  valueFormatters.multiCurrencyWholeNumberWithSymbol,
];

export const isExpressionBasedCurrencyFormatter = (
  f: ValueFormatter | CurrencyFormatter | ExpressionBasedCurrencyFormatter
): f is ExpressionBasedCurrencyFormatter => f === valueFormatters.expressionBasedCurrencyWithSymbol;

export const isCurrencyFormatter = (
  f: ValueFormatter | CurrencyFormatter | ExpressionBasedCurrencyFormatter
): f is CurrencyFormatter => currencyFormatters.includes(f);

export const isValueFormatter = (
  f: ValueFormatter | CurrencyFormatter | ExpressionBasedCurrencyFormatter
): f is ValueFormatter => !isCurrencyFormatter(f) && !isExpressionBasedCurrencyFormatter(f);

export const getValueFormatterForSVG = (columnType: ValueFormatterName, currency: string): ValueFormatter => {
  const valueFormatter = valueFormatters[columnType];
  // Both expression-based and regular currency formatters can be called with just currency
  if (isExpressionBasedCurrencyFormatter(valueFormatter) || isCurrencyFormatter(valueFormatter)) {
    return valueFormatter(currency);
  }
  return valueFormatter;
};

export const getValueFormatter = (format: ColumnDefV2Format, currency: string | undefined): ValueFormatter | null => {
  const formatter = columnDefValueFormatters[format];

  if (isCurrencyFormatter(formatter) && currency) {
    return formatter(currency);
  }

  if (!isCurrencyFormatter(formatter) && !isExpressionBasedCurrencyFormatter(formatter)) {
    return formatter;
  }

  return null;
};

export const selectValueFormatterFromColDef = (args: SelectFormatterArgs): ValueFormatter | null => {
  const { format, currency } = args;
  if (!format) {
    return null;
  }

  return getValueFormatter(format, currency);
};

const percentageFormatters: (ValueFormatter | CurrencyFormatter)[] = [
  valueFormatters.percentage,
  valueFormatters.percentageWithoutSymbol,
  valueFormatters.decimalAsPercentage,
  valueFormatters.decimalAsPercentageWithoutSymbol,
  valueFormatters.oneDecimalAsPercentage,
  valueFormatters.oneDecimalAsPercentageWithoutSymbol,
  valueFormatters.zeroDecimalsAsPercentage,
  valueFormatters.zeroDecimalsAsPercentageWithoutSymbol,
];

export const isPercentageFormatter = (f: ValueFormatter | CurrencyFormatter): boolean =>
  percentageFormatters.includes(f);

export const formattersWithSymbol: (ValueFormatter | CurrencyFormatter | ExpressionBasedCurrencyFormatter)[] = [
  valueFormatters.currencyWithSymbol,
  valueFormatters.currencyWithSymbolPostfix,
  valueFormatters.currencyAuto,
  valueFormatters.currencyShorthandWithSymbol,
  valueFormatters.currencyWholeNumberWithSymbol,
  valueFormatters.expressionBasedCurrencyWithSymbol,
  valueFormatters.multiCurrencyWithSymbol,
  valueFormatters.multiCurrencyWithSymbolPostfix,
  valueFormatters.multiCurrencyAuto,
  valueFormatters.multiCurrencyShorthandWithSymbol,
  valueFormatters.multiCurrencyWholeNumberWithSymbol,
  valueFormatters.percentage,
  valueFormatters.decimalAsPercentage,
  valueFormatters.oneDecimalAsPercentage,
];

export const isFormatterWithSymbol = (
  f: ValueFormatter | CurrencyFormatter | ExpressionBasedCurrencyFormatter
): boolean => formattersWithSymbol.includes(f);

/**
 * In JS, 0 === -0, but formatting a negative zero will also give you a negative zero. So this function converts
 * negative zero to regular zero
 * @param num
 */
export function convertPossibleNegZeroToZero(num: number) {
  if (num === 0 && 1 / num === -Infinity) {
    return 0;
  }
  return num;
}

/**
 * Detects and removes IEEE-754 floating-point noise from a decimal string.
 *
 * ## How it works
 *
 * JavaScript numbers use IEEE-754 binary floating-point, which cannot represent most
 * decimal fractions exactly. When these imprecise numbers are fed into arbitrary-precision
 * libraries like `decimal.js`, the tiny binary errors get preserved and amplified through
 * multiplication/division, producing results with telltale noise patterns:
 *
 * - **Trailing zeros + stray digits**: `"30577714.68000000362"` — the true value is `~.68`,
 *   but binary imprecision adds `000000362`.
 * - **Trailing nines + stray digits**: `"30577714.619999999486"` — the true value is `~.62`,
 *   but binary imprecision subtracts a tiny amount, producing `9999994864`.
 * - **Interleaved nines at end**: `"30680976.00997999"` — the user entered `30680976.00997`,
 *   but JS loop arithmetic produced trailing nines with nothing after them.
 * - **Interleaved nines with stray digits**: `"30680976.009979993188"` — same root cause,
 *   but additional stray digits follow the nines block.
 *
 * This function detects these patterns using the regex `/\.\d{3,}0{4,}\d*$|\.\d{3,}9{4,}\d*$/`:
 * - Requires a decimal point followed by 3+ significant digits (guard against false positives
 *   on small legitimate values like `0.00001`).
 * - Then 4+ consecutive zeros OR 4+ consecutive nines.
 * - Followed by zero or more additional digits (`\d*`) — covers both trailing-noise-at-end
 *   and trailing-noise-with-stray-digits patterns.
 * When detected, it iteratively rounds down from `maxDecimalPlaces` (default 9) until the
 * noise pattern no longer appears. If no noise is detected, the original string is returned untouched.
 *
 * Threshold rationale: client-entered security market values are expected to have at most
 * 3 decimal places. FX round-trip arithmetic (USD → EUR → USD) and JS loop calculations
 * introduce spurious digits via IEEE-754 floating-point errors, producing patterns such as
 * `"30680976.568000018138"` when the user entered `"30680976.57"`. Values like `"0.00001"`
 * and `"1.2300001"` are left untouched because the noise pattern only fires after 3+
 * significant decimal digits.
 *
 * @param decimalString - The string representation of a decimal number to check
 * @param maxDecimalPlaces - Decimal places to round to when noise is detected (default: 9)
 * @returns The original string if clean, or the rounded string if noise was detected
 */
export function cleanFloatingPointNoise(decimalString: string, maxDecimalPlaces = 9): string {
  const noisePattern = /\.\d{3,}0{4,}\d*$|\.\d{3,}9{4,}\d*$/;
  if (!noisePattern.test(decimalString)) {
    return decimalString;
  }
  for (let dp = maxDecimalPlaces; dp >= 2; dp--) {
    const rounded = new Decimal(decimalString).toDecimalPlaces(dp).toString();
    if (!noisePattern.test(rounded)) {
      return rounded;
    }
  }
  return new Decimal(decimalString).toDecimalPlaces(2).toString();
}

export function sanitizeNumber(num: number, decimalPlaces?: number) {
  const roundedValue = roundFloatingPointError(num, decimalPlaces);
  return convertPossibleNegZeroToZero(roundedValue);
}

/**
 * Returns the number of decimal digits in a number or string.
 * Handles both number and string input. Does not handle scientific notation.
 *
 * @param value number or string to check
 * @returns number of digits after the decimal point
 */
export const getDecimalDigits = (value: number | string): number => {
  const str = typeof value === 'number' ? value.toString() : value;
  const arr = str.split('.');
  if (arr.length > 1) {
    return (arr as [string, string])[1].length;
  }
  return 0;
};

/**
 * @description
 * the function first extracts the list of numerical value from the data source
 * then it converts those number into string and split them based on the decimal divider "."
 * after that it gets the length of the string in the decimal places and use it to calculate the median
 *
 * @param data the input data source to perform decimal calculation
 * @param datumFunc the function which extracts the numerical value for decimal calculation
 * @returns the median of the decimal digit based on the numerical data
 */
export const calculateDecimalDigits = <T>(data: T[], datumFunc: (datum: T) => number): number => {
  const decimalDigitsArray: number[] = data
    .map((datum) => String(datumFunc(datum)))
    .map(getDecimalDigits)
    .sort();
  return decimalDigitsArray[Math.floor(decimalDigitsArray.length / 2)] ?? 0;
};

export const roundToTwoDecimals = (value: number) => Math.round(value * 100) / 100;
export const roundToThreeDecimals = (value: number) => Math.round(value * 1000) / 1000;

/**
 * @description
 * the function is the same as value % divisor === 0 but supports float
 * @param value the number being divided, i.e., dividend
 * @param divisor the number the dividend is divided by
 * @returns if dividend is a multiple of the divisor
 */
export const isValueAMultiple = (value: number, divisor: number) => {
  const quotient = Math.floor(roundFloatingPointError(value / divisor));
  const multiple = roundFloatingPointError(quotient * divisor);
  return roundFloatingPointError(value - multiple) === 0;
};

const isColumnDefV2Format = (format: string): format is ColumnDefV2Format => format in columnDefValueFormatters;

/**
 * Extracts the effective decimal places from a numeral.js format string.
 * For percentage formats, adds 2 to account for the *100 multiplication.
 */
export function getFormatDecimalPlaces(format: string): number {
  const isPercent = format.includes('%');
  const regex = /\.([0#]+)/;
  const match = regex.exec(format);
  const formatDecimals = match ? match[1].length : 0;
  return isPercent ? formatDecimals + 2 : formatDecimals;
}

/**
 * Sanitizes a value to prevent numeral.js negative-zero formatting bugs.
 * If the absolute value is below the rounding threshold for the format,
 * returns 0 to avoid "-$0.00" or "0$.00" output.
 */
export function sanitizeValueForFormat(value: number, format: string): number {
  const decimals = getFormatDecimalPlaces(format);
  const threshold = Math.pow(10, -(decimals + 1)) * 5;
  if (Math.abs(value) < threshold) {
    return 0;
  }
  return value;
}

/**
 * Replaces $ symbol in formatted string with the override symbol.
 * Handles both positive ("$1,234") and negative ("-$1,234") values.
 */
const replaceDollarSign = (formattedValue: string, overrideSymbol: string): string =>
  formattedValue.replace('$', overrideSymbol);

export const numeralJsFormatter = (
  value?: string | number | null,
  format?: string,
  currency?: string,
  currencySymbol?: string
): string | undefined => {
  if ((isNumber(value) && isNaN(value)) || isNil(value)) {
    return undefined;
  }

  if ((typeof value === 'number' && isNumberInvalid(value)) || !format) {
    return (value ?? '').toString();
  }

  if (isColumnDefV2Format(format)) {
    if (isNumber(value)) {
      const formatter = getValueFormatter(format, currency);
      if (formatter) {
        return formatter(value);
      }
    }
    return undefined;
  }

  const sanitizedValue = isNumber(value) ? sanitizeValueForFormat(value, format) : value;
  const result = numeral(sanitizedValue).format(format);

  // Replace $ with currencySymbol if provided and result contains $
  if (currencySymbol && result.includes('$')) {
    return replaceDollarSign(result, currencySymbol);
  }
  return result;
};
